import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Download, Printer, Loader2 } from 'lucide-react'

interface BadgePreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pdfBlob: Blob | null
  filename: string
}

export const BadgePreviewDialog: React.FC<BadgePreviewDialogProps> = ({
  open,
  onOpenChange,
  pdfBlob,
  filename,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    if (!pdfBlob) {
      setObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(pdfBlob)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [pdfBlob])

  const handleDownload = () => {
    if (!objectUrl) return
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    a.click()
  }

  const handlePrint = () => {
    iframeRef.current?.contentWindow?.print()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Badge Preview</DialogTitle>
          <DialogDescription>
            Preview the generated badges before printing or downloading.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 border rounded overflow-hidden bg-muted">
          {objectUrl ? (
            <iframe
              ref={iframeRef}
              src={objectUrl}
              className="w-full h-full"
              title="Badge preview"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleDownload} disabled={!objectUrl}>
            <Download className="mr-2 h-4 w-4" />
            Download
          </Button>
          <Button onClick={handlePrint} disabled={!objectUrl}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
